import React from "react";
import { SectionHeader } from "./SectionHeader";
import { Box } from "@mui/system";
import GenericBarGraph from "@/components/Graph/GenericBarGraph";
import OrderStatusBreakdown from "./OrderStatusBreakdown";
import { ChartBox } from "./ChartBox";
import Table from "@/components/Table/Table";
import type { OrderMetricsProps } from "@/types/dashboardtypes";

const chartItemSx = (singleColumn?: boolean) =>
  singleColumn ? { minWidth: 0, maxWidth: "100%", width: "100%" } : {};

export default function OrderMetrics(props: OrderMetricsProps) {
  const {
    TotalOrderAmountByYearLabels,
    TotalOrderAmountByYearDatasets,
    labels,
    datasets,
    mostOrderedColumn,
    mostOrderedProductList,
    ProductByCategoryLabels,
    ProductByCategoryDatasets,
    OrderCancellationPercentageLabels,
    OrderCancellationPercentageDatasets,
    TotalDiscountPercentageLabels,
    TotalDiscountPercentageDatasets,
    MaxMinOrderData,
    lastOrderData,
    singleColumn,
  } = props;

  const compact = !!singleColumn;

  return (
    <Box
      sx={{
        mt: 3,
        borderRadius: 2,
        overflow: singleColumn ? "visible" : "hidden",
        boxShadow: 1,
        maxWidth: "100%",
      }}
    >
      <SectionHeader title="Order Metrics" />
      <Box
        sx={{
          bgcolor: "#fff",
          p: singleColumn ? 1 : 2,
          display: "grid",
          gridTemplateColumns: singleColumn ? "1fr" : "1fr 1fr",
          gap: singleColumn ? 2 : 3,
          minWidth: 0,
          maxWidth: "100%",
          "& > *": { minWidth: 0 },
        }}
      >
        <Box sx={chartItemSx(singleColumn)}>
          <ChartBox title="">
            <GenericBarGraph
              datasets={TotalOrderAmountByYearDatasets}
              labels={TotalOrderAmountByYearLabels}
              title="Total Order Received Amount"
              compact={compact}
            />
          </ChartBox>
        </Box>

        <Box sx={chartItemSx(singleColumn)}>
          <GenericBarGraph
            datasets={datasets}
            labels={labels}
            title="Order By Status"
            compact={compact}
          />
        </Box>

        <Box sx={chartItemSx(singleColumn)}>
          <ChartBox title="Most Ordered Products">
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Table
                columns={mostOrderedColumn}
                data={mostOrderedProductList || []}
              />
            </Box>
          </ChartBox>
        </Box>

        <Box sx={chartItemSx(singleColumn)}>
          <ChartBox>
            <GenericBarGraph
              title="Most Ordered Category"
              datasets={ProductByCategoryDatasets}
              labels={ProductByCategoryLabels}
              compact={compact}
            />
          </ChartBox>
        </Box>

        <Box sx={chartItemSx(singleColumn)}>
          <GenericBarGraph
            title="Order Cancellation Rate (%)"
            datasets={OrderCancellationPercentageDatasets}
            labels={OrderCancellationPercentageLabels}
            compact={compact}
          />
        </Box>

        <Box sx={chartItemSx(singleColumn)}>
          <GenericBarGraph
            title="Order Discount Rate (%)"
            datasets={TotalDiscountPercentageDatasets}
            labels={TotalDiscountPercentageLabels}
            compact={compact}
          />
        </Box>
      </Box>

      <OrderStatusBreakdown
        data={MaxMinOrderData}
        lastOrderData={lastOrderData}
        singleColumn={compact}
      />
    </Box>
  );
}
